import React from 'react'

import { css, cx } from '@portal/styled-system/css'

import { useToggle } from '@/shared/lib/hooks'

import { Input } from '../view'

type PasswordInputProps = Omit<React.ComponentProps<typeof Input>, 'type' | 'endDecorator'>

export function PasswordInput(props: PasswordInputProps) {
    const [isPasswordVisible, togglePasswordVisibility] = useToggle(false)
    const inputRef = React.useRef<HTMLInputElement | null>(null)

    return (
        <Input
            {...props}
            ref={(node) => {
                inputRef.current = node
                setRef(props.ref, node)
            }}
            type={isPasswordVisible ? 'text' : 'password'}
            classNameInput={css({
                '&[type="password"]:not(:placeholder-shown)': { fontFamily: 'notoSans' },
            })}
            endDecorator={
                <ToggleButton isPasswordVisible={isPasswordVisible} onClick={togglePasswordVisibility} ref={inputRef} />
            }
        />
    )
}

type ToggleButtonProps = {
    isPasswordVisible: boolean
    onClick: () => void
    ref: React.RefObject<HTMLInputElement | null>
}

function ToggleButton({ isPasswordVisible, onClick, ref }: ToggleButtonProps) {
    const handleClick = () => {
        onClick()

        const inputElement = ref.current
        if (!inputElement) return

        const { selectionStart, selectionEnd } = inputElement

        // NOTE: Restore caret position after toggling password visibility
        window.requestAnimationFrame(() => {
            if (selectionStart !== null && selectionEnd !== null) {
                inputElement.setSelectionRange(selectionStart, selectionEnd)
            }
        })
    }

    return (
        <div
            role="button"
            onClick={handleClick}
            onMouseDown={(e) => {
                e.preventDefault() // NOTE: Does not allow to take focus away from the input
            }}
            className={css({
                width: '5.5',
                height: '5',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                color: 'gray.400',
                _hover: { color: 'gray.600/50' },
            })}
        >
            <i className={cx(`fa-solid fa-eye${isPasswordVisible ? '' : '-slash'}`, css({ fontSize: 'md' }))} />
        </div>
    )
}

function setRef<T>(ref: React.Ref<T> | undefined, value: T | null) {
    if (typeof ref === 'function') {
        ref(value)
    } else if (ref) {
        ref.current = value
    }
}
